<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditUz.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditUz.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditUz.title') }}</li>
                    </ol>
                </div>
            </div>

            <form ref="formRef" id="uzForm" name="uzForm" novalidate @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-10">
                        <div class="card">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('componentEditUz.section') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-md-6">
                                        <label for="uz-label" class="form-label">{{ $t('componentEditUz.readerName') }}</label>
                                        <input id="uz-label" type="text" class="form-control" v-model="form.label" maxlength="32"
                                            :placeholder="$t('componentEditUz.readerNamePlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="uz-door-label" class="form-label">{{ $t('componentEditUz.doorName') }}</label>
                                        <input id="uz-door-label" type="text" class="form-control" v-model="form.door_label" maxlength="32"
                                            :placeholder="$t('componentEditUz.doorNamePlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-12">
                                        <label for="uz-hub" class="form-label">{{ $t('componentEditUz.hub') }}</label>
                                        <div class="input-group">
                                            <select id="uz-hub" ref="hubSelect" class="form-select" v-model="selectedIp" @change="onHubChange">
                                                <option value="" disabled>{{ $t('componentEditUz.selectHub') }}</option>
                                                <option v-for="gateway in gateways" :key="gateway.ip" :value="gateway.ip">{{ gateway.name }} ({{ gateway.ip }})</option>
                                            </select>
                                            <button id="uz-add-hub" type="button" class="btn btn-outline-primary" :title="$t('componentEditUz.addHub')" :aria-label="$t('componentEditUz.addHub')" @click="addHub">
                                                <i class="mdi mdi-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div class="text-center mt-2">
                                    <button id="uz-pair" type="submit" class="btn btn-primary" :disabled="pairing">
                                        <span v-if="pairing" class="spinner-border spinner-border-sm me-1" role="status"></span>{{ $t('componentEditUz.startPairing') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
                </div>
            </form>
        </div>

        <GatewayModal ref="gatewayModal" />
        <ConfirmationModal ref="confirmModal" :title="$t('componentEditUz.startPairing')" :question="$t('componentEditUz.presentServiceBadge')" type="primary"
            :button1="$t('common.cancel')" :button2="$t('componentEditUz.startPairing')" :action="pair" />
    </div>
</template>

<script>
import { computed, nextTick, reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import GatewayModal from '@/modals/gatewayModal.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';

// UZ integration of a LINK: pairs a UZ product through a UZ hub, the product becomes a reader of the LINK
export default {
    name: 'ComponentEditUz',
    components: {
        GatewayModal,
        ConfirmationModal,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const formRef = ref(null);
        const hubSelect = ref(null);
        const gatewayModal = ref(null);
        const confirmModal = ref(null);
        const pairing = ref(false);
        const form = reactive({ label: '', door_label: '' });

        // Hubs of the UZ products already paired, the first one selected as in the app
        const gateways = ref(hub.getMecaGateways('UZ'));
        const selectedIp = ref(gateways.value.length > 0 ? gateways.value[0].ip : '');
        const selectedGateway = computed(() => gateways.value.find(g => g.ip === selectedIp.value));

        const pathToInstallationEditComponents = computed(() => `/installation-edit?id=${facilityId}&action=components`);

        const onHubChange = (event) => {
            event.target.setCustomValidity('');
        };

        const addHub = async () => {
            const gateway = await gatewayModal.value.show(t('componentEditUz.addHub'));
            if (!gateway) return;
            if (!gateways.value.some(g => g.ip === gateway.ip)) gateways.value.push(gateway);
            selectedIp.value = gateway.ip;
            hubSelect.value.setCustomValidity('');
        };

        // The product is paired once the installer presents the SERVICE badge to it
        const handleSubmit = async () => {
            if (pairing.value) return;
            form.label = form.label.trim();
            form.door_label = form.door_label.trim();
            await nextTick();
            hubSelect.value.setCustomValidity(selectedGateway.value ? '' : t('componentEditUz.selectHub'));
            if (!formRef.value.checkValidity()) {
                formRef.value.reportValidity();
                return;
            }
            confirmModal.value.show();
        };

        const pair = async () => {
            pairing.value = true;
            try {
                await userStore.pairUZ(facilityId, hub, {
                    label: form.label,
                    door_label: form.door_label,
                    gateway_ip: selectedGateway.value.ip,
                    gateway_name: selectedGateway.value.name,
                });
                // The components list reloads the LINK with its new reader
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('componentEditUz.pairingSuccess'));
            } catch (message) {
                showToast('error', message);
            } finally {
                pairing.value = false;
            }
        };

        return {
            formRef,
            hubSelect,
            gatewayModal,
            confirmModal,
            pairing,
            form,
            gateways,
            selectedIp,
            onHubChange,
            addHub,
            handleSubmit,
            pair,
            pathToInstallationEditComponents,
        };
    },
};
</script>
